import React from "react"
import usePublicCounters from "../hooks/usePublicCounters.js"
import TickingNumber from "../components/TickingNumber.jsx"
import CountersStrip from "../components/CountersStrip.jsx"
import Reveal from "../components/Reveal.jsx"
import { formatterFor, rawValueFor } from "../lib/format.js"

// Cold-email-tuned landing page. Linked from the email body via the
// `audit` Outreach::Campaign. Rewritten 2026-05-19 after AJ caught
// the original copy lying — promising a manual audit Shopshift doesn't
// deliver. Shopshift IS the audit, automated, during the trial. Hero
// CTA above the fold so cold-email visitors don't have to scroll.

function Hero() {
  const { byName } = usePublicCounters()
  const headline = byName.get("additional_conversions")

  return (
    <section className="max-w-4xl mx-auto px-6 pt-16 pb-12">
      <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ai)] mb-6">
        § 01 · Your audit, automated
      </p>

      <h1 className="font-sans font-medium text-[color:var(--color-ink-primary)] text-[56px] md:text-[72px] tracking-[-0.045em] leading-[0.98]">
        Shopshift runs the audit. Continuously.
      </h1>

      <p className="mt-8 font-sans text-[20px] tracking-[-0.015em] text-[color:var(--color-ink-secondary)] max-w-2xl leading-[1.45]">
        The note you got was the human-readable summary of one thing Shopshift's AI noticed about your store.
        Start the trial and the same AI walks your homepage, product pages, and checkout end-to-end — then keeps
        watching, picking experiments, and shipping the winners. No consultant on the other end. Just software.
      </p>

      <div className="mt-10 flex items-center gap-4 flex-wrap">
        <a
          href="/users/sign_up"
          className="inline-flex items-center bg-[color:var(--color-brand)] text-white font-sans text-[15px] font-medium px-6 py-3.5 rounded-[var(--radius-button)] hover:opacity-90 transition-opacity"
        >
          Start your 30-day free trial
        </a>
        <a
          href="#how-it-works"
          className="font-sans text-[14px] font-medium text-[color:var(--color-ai)] hover:underline"
        >
          How it works ↓
        </a>
      </div>
      <p className="mt-4 font-mono text-[11px] tracking-[0.04em] text-[color:var(--color-ink-faint)]">
        Card required · no charge during trial · cancel any time
      </p>

      <div className="mt-12 inline-flex items-baseline gap-3 font-mono text-[12px] tracking-[0.04em] text-[color:var(--color-ink-tertiary)]">
        <span
          className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--color-winner-dot)]"
          style={{ animation: "pulse 2s ease-in-out infinite" }}
        />
        Shopshift has caused <TickingNumber value={rawValueFor(headline)} format={formatterFor(headline?.unit)} /> additional conversions across the merchants running it.
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </section>
  )
}

function HowItWorks() {
  const steps = [
    {
      label: "Day 1",
      title: "Shopshift learns your store",
      body: "The script crawls your top pages, learns your tone + product catalog, classifies every visitor source, and surfaces the biggest CRO frictions it found."
    },
    {
      label: "Day 2-7",
      title: "First experiments go live",
      body: "Shopshift picks the highest-expected-lift opportunity, writes the A/B test, and ships it. You see every proposal before it runs — or set the autonomy higher and let it self-ship."
    },
    {
      label: "Continuous",
      title: "It keeps doing it",
      body: "Once a test reaches Bayesian significance, the winner auto-applies and the AI picks the next angle. No reports to read, no audits to schedule. Just steady conversion improvement."
    }
  ]

  return (
    <section id="how-it-works" className="max-w-4xl mx-auto px-6 py-20 border-t border-[color:var(--color-rule)]">
      <Reveal>
        <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ai)] mb-6">
          § 02 · How the audit runs
        </p>
        <h2 className="font-sans font-medium text-[color:var(--color-ink-primary)] text-[40px] tracking-[-0.035em] leading-[1.05]">
          Three phases, all automatic.
        </h2>
      </Reveal>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-10">
        {steps.map((step, idx) => (
          <Reveal key={step.label} delay={120 * idx}>
            <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[color:var(--color-ink-tertiary)] mb-3">
              {step.label}
            </p>
            <h3 className="font-sans font-medium text-[color:var(--color-ink-primary)] text-[22px] tracking-[-0.025em] leading-[1.2]">
              {step.title}
            </h3>
            <p className="mt-3 font-sans text-[15px] text-[color:var(--color-ink-secondary)] leading-[1.55]">
              {step.body}
            </p>
          </Reveal>
        ))}
      </div>
    </section>
  )
}

function ClosingCta() {
  return (
    <section className="max-w-4xl mx-auto px-6 py-24 border-t border-[color:var(--color-rule)]">
      <Reveal>
        <h2 className="font-sans font-medium text-[color:var(--color-ink-primary)] text-[40px] tracking-[-0.035em] leading-[1.05] max-w-2xl">
          Start the trial. The audit happens on day one.
        </h2>
        <p className="mt-6 font-sans text-[17px] text-[color:var(--color-ink-secondary)] leading-[1.6] max-w-2xl">
          30 days free. Card required so Shopshift can start running tests immediately, but you're not charged
          during the trial, and you can cancel any time.
        </p>
      </Reveal>

      <div className="mt-10 flex items-center gap-4 flex-wrap">
        <a
          href="/users/sign_up"
          className="inline-flex items-center bg-[color:var(--color-brand)] text-white font-sans text-[15px] font-medium px-6 py-3.5 rounded-[var(--radius-button)] hover:opacity-90 transition-opacity"
        >
          Start the free trial
        </a>
        <a
          href="/pricing"
          className="font-sans text-[14px] font-medium text-[color:var(--color-ai)] hover:underline"
        >
          Pricing details →
        </a>
      </div>
    </section>
  )
}

export default function AuditView() {
  return (
    <>
      <Hero />
      <HowItWorks />
      <CountersStrip />
      <ClosingCta />
    </>
  )
}
